/**
 * Game session: state of a single battle.
 * Domain-only; no ports. ADR-7: mana = life. ADR-8: speed 0/1/3.
 */

import type { Cell, BuildingType, CombineGemError, FieldConfig, FieldId, GemType, MonsterType, PathBlocked } from "./types.js";
export type { PathBlocked };
import type { Field, Gem, GemMods, Mana, Monster, Tower, Wave, WaveSpawner, SpeedMultiplier, Shot, HitFlash } from "./entities.js";
export { waveTotal, waveSpawned, waveDone } from "./wave-helpers.js";
import { waveTotal, waveDone } from "./wave-helpers.js";
import {
  APEX_DMG_MULT,
  APEX_FIRE_RATE_MULT,
  APEX_PRIMARY_PENALTY,
  APEX_SECONDARY_MAGNITUDE,
  APEX_SECONDARY_PROC_BASE,
  DUAL_EFFECT_PENALTY,
} from "./combine-constants.js";
import {
  getTowerTarget,
  computeDamage,
  getFireRateMs,
} from "./tower-shooting.js";
export { AMPLIFIER_RADIUS, AMPLIFIER_FIRE_RATE_BONUS } from "./tower-shooting.js";
import { nextCellTowardsTarget, computeFullPath, bfsInAllowed, bfsReachable } from "./pathfinding.js";
import { applyGemEffectOnHit, rollCritMultiplier, PURPLE_SLOW_RATIO, nextRandom } from "./gem-effects.js";
import {
  type StageSettings,
  createDefaultStageSettings,
  getHpMultiplier,
  getExtraWaves,
  getBanishMultiplierGrowth,
} from "./stage-settings.js";
import { MONSTER_SPECS } from "./monster-specs.js";
import { GEM_SPECS } from "./gem-specs.js";

export type { SpeedMultiplier };
export type { StageSettings };

/** Skill bonuses derived from the mage profile. All values are %-based integers. */
export interface SkillEffects {
  /** % bonus to starting mana and max mana */
  moreInitialMana: number;
  /** % bonus to mana regen per ms and kill rewards */
  manaReplenish: number;
  /** % discount on banish cost */
  banishManaCost: number;
  /** % discount on gem create and combine mana cost */
  lowerGemCost: number;
  /** % discount on tower/wall/amplifier mana cost */
  buildingCost: number;
  /** % bonus to demolish refund (above base 50%) */
  demolition: number;
  /** % bonus to Green poison damage and duration */
  poisonMastery: number;
  /** % bonus to Red crit chance and crit multiplier */
  bloodboundMastery: number;
  /** % bonus to bomb and Orange splash explosive damage */
  violentExplosions: number;
  /** % bonus to Orange splash chance and damage */
  multipleDamageMastery: number;
  /** % bonus to Lime chain chance and damage */
  chainHitMastery: number;
  /** % bonus to amplifier fire rate effect */
  amplifierPower: number;
  /** % bonus to max mana/resource pool */
  cheaperManaPoolAndUnlock: number;
  /** % bonus to dual-gem primary and secondary effect strength */
  dualGemMastery: number;
  /** % chance per level to get level-3 gem on create */
  tripleGemMastery: number;
  /** % bonus to Purple slow strength and duration */
  slowMastery: number;
  /** % bonus to Blue armor tearing amount */
  armorTearingMastery: number;
  /** % bonus to base gem damage */
  damage: number;
  /** % bonus to Cyan shock chance and duration */
  shockingMastery: number;
  /** % bonus to Yellow mana/resource gain on hit */
  manaGatheringMastery: number;
  /** % bonus to trap damage and trap-carried effects */
  traps: number;
  /** Level of the free random starter gem, 0 when absent */
  wildStarterGem: number;
  /** % reduction target for gem deployment delay; currently stored for future deployment-delay hook */
  gemDeploymentTime: number;
  /** % bonus to gem firing speed */
  firingSpeed: number;
  /** % bonus to tower range */
  range: number;
  /** Flat starting mana/resource bonus derived from completed fields */
  badgesToMana: number;
  /** % bonus to shrine discharge damage */
  shrines: number;
}

const DEFAULT_SKILL_EFFECTS: SkillEffects = {
  moreInitialMana: 0,
  manaReplenish: 0,
  banishManaCost: 0,
  lowerGemCost: 0,
  buildingCost: 0,
  demolition: 0,
  poisonMastery: 0,
  bloodboundMastery: 0,
  violentExplosions: 0,
  multipleDamageMastery: 0,
  chainHitMastery: 0,
  amplifierPower: 0,
  cheaperManaPoolAndUnlock: 0,
  dualGemMastery: 0,
  tripleGemMastery: 0,
  slowMastery: 0,
  armorTearingMastery: 0,
  damage: 0,
  shockingMastery: 0,
  manaGatheringMastery: 0,
  traps: 0,
  wildStarterGem: 0,
  gemDeploymentTime: 0,
  firingSpeed: 0,
  range: 0,
  badgesToMana: 0,
  shrines: 0,
};

export interface SessionStats {
  killsThisBattle: number;
  banishesThisBattle: number;
  bombsCastThisBattle: number;
  /** battle start time in terms of elapsedMs (always 0 at session creation). */
  startedAt: number;
  /** AS3 StatId.WAVES_STARTED_EARLY analogue: successful early hurry actions this battle. */
  earlyHurryCount: number;
  /** AS3 MANA_GAINED_FROM_WAVESPEEDING (stage) — light gained from hurrying spawns this battle. */
  manaFromEarlyHurry: number;
}

export interface GameSession {
  field: Field;
  mana: Mana;
  waves: Wave[];
  currentWaveIndex: number;
  monsters: Monster[];
  elapsedMs: number;
  towers: Tower[];
  gemInventory: Gem[];
  speed: SpeedMultiplier;
  won: boolean | null;
  towersBuilt: number;
  wallsBuilt: number;
  amplifiersBuilt: number;
  trapsBuilt: number;
  shrinesBuilt: number;
  hpGrowthRate: number;
  armorGrowthRate: number;
  monsterBaseHp: number;
  monstersPerWave: number;
  /** In-flight projectiles (plan 08). */
  shots: Shot[];
  /** Transient hit-flash visuals (plan 08). */
  hitFlashes: HitFlash[];
  /** mulberry32 state for deterministic gem effects (plan 09). Initialized on first use. */
  rngSeed?: number;
  /** Active skill bonuses for this session. */
  skillEffects: SkillEffects;
  /** Battle settings chosen before starting (difficulty modifiers). */
  stageSettings: StageSettings;
  /** Original wavesNum from config BEFORE Battle Settings extraWaves. Used for base XP calculation. */
  originalWavesNum: number;
  /** Original monsterBaseHp from config BEFORE Battle Settings HP multiplier. Used for base XP calculation. */
  originalMonsterBaseHp: number;
  /** Per-battle stats accumulated during the session. */
  sessionStats: SessionStats;
  /** Full BFS path (cell-by-cell) for rendering the road. Recomputed on terrain changes. */
  pathCells: Cell[];
}

// --- Constants (AS3-faithful: GameSpecs, IngameInitializer) ---

export const STARTING_MANA = 300;
export const STARTING_MAX_MANA = 1600;

export const GEM_CREATE_COST = 77;
export const MAX_GEM_LEVEL = 9;
export const INVENTORY_MAX = 4; // B16: shrunk 3×4 → 2×2
const GEM_COMBINE_COST = 77;

export const TOWER_BASE_COST = 150;
export const TOWER_COST_INCREMENT = 30;

export const WALL_BASE_COST = 30;
export const WALL_COST_INCREMENT = 1;

export const AMPLIFIER_BASE_COST = 400;
export const AMPLIFIER_COST_INCREMENT = 240;

export const TRAP_BASE_COST = 150;
export const TRAP_COST_INCREMENT = 24;
export const TRAP_RANGE_CELLS = 0.75;
export const TRAP_FIRE_RATE_MULT = 1 / 1.5;
export const TRAP_DAMAGE_MULT = 0.2;
export const TRAP_EFFECT_MULT = 1.35;

export const SHRINE_BASE_COST = 900;
export const SHRINE_COST_INCREMENT = 500;
export const SHRINE_CHARGE_MS = 15000;
export const SHRINE_RADIUS_CELLS = 4;
export const SHRINE_DAMAGE_BASE = 40;

export const SPEED_MS_PER_CELL = 500;
export const FIRE_RATE_MS = 1000;

/** Projectile speed in cells/ms (plan 08). 0.008 = 8 cells/sec. */
export const SHOT_SPEED_CELLS_PER_MS = 0.008;

/** Hit-flash duration (plan 08). */
export const HIT_FLASH_DURATION_MS = 200;

const DEFAULT_WAVES_NUM = 8;
const DEFAULT_MONSTERS_PER_WAVE = 10;
const DEFAULT_SPAWN_INTERVAL_MS = 800;
const DEFAULT_MONSTER_BASE_HP = 10;
const DEFAULT_ARMOR_GROWTH_RATE = 0.11;

// --- HP growth rate table (AS3: IngamePopulator) ---

export function hpGrowthRateForWaveCount(n: number): number {
  if (n < 10) return 1.23;
  if (n < 15) return 1.18;
  if (n < 20) return 1.15;
  if (n < 25) return 1.126;
  if (n < 30) return 1.114;
  if (n < 35) return 1.1;
  if (n < 40) return 1.094;
  if (n < 50) return 1.085;
  if (n < 60) return 1.083;
  if (n < 70) return 1.074;
  return 1.051;
}

// --- Gem costs (AS3: grade 1 = 77, grade n+1 = prev*2 + 77) ---

export function gemUpgradeCost(level: number): number {
  let cost = GEM_CREATE_COST;
  for (let i = 1; i < level; i++) {
    cost = Math.floor(cost * 2 + GEM_COMBINE_COST);
  }
  return cost;
}

export function gemDuplicateCost(level: number): number {
  return gemUpgradeCost(level);
}

/** Effective gem create cost with LOWER_CREATING_AND_COMBINING_MANA_COST skill discount. */
export function effectiveGemCreateCost(session: GameSession): number {
  return Math.max(1, Math.floor(GEM_CREATE_COST * (1 - 0.01 * session.skillEffects.lowerGemCost)));
}

export function gemRecycleValue(level: number): number {
  return Math.floor(0.7 * gemUpgradeCost(level));
}

// --- Tower cost (AS3: 150 + 30*n) ---

export function towerCost(towersBuilt: number): number {
  return TOWER_BASE_COST + TOWER_COST_INCREMENT * towersBuilt;
}

// --- Wall cost (AS3: 30 + 1*n) ---

export function wallCost(wallsBuilt: number): number {
  return WALL_BASE_COST + WALL_COST_INCREMENT * wallsBuilt;
}

// --- Amplifier cost (AS3: 400 + 240*n) ---

export function amplifierCost(amplifiersBuilt: number): number {
  return AMPLIFIER_BASE_COST + AMPLIFIER_COST_INCREMENT * amplifiersBuilt;
}

// --- Trap cost (AS3: 150 + 24*n) ---

export function trapCost(trapsBuilt: number): number {
  return TRAP_BASE_COST + TRAP_COST_INCREMENT * trapsBuilt;
}

/** Effective tower cost with BUILDING_COST skill discount. */
export function effectiveTowerCost(session: GameSession): number {
  return Math.max(1, Math.floor(towerCost(session.towersBuilt) * (1 - 0.01 * session.skillEffects.buildingCost)));
}

/** Effective wall cost with BUILDING_COST skill discount. */
export function effectiveWallCost(session: GameSession): number {
  return Math.max(1, Math.floor(wallCost(session.wallsBuilt) * (1 - 0.01 * session.skillEffects.buildingCost)));
}

/** Effective amplifier cost with BUILDING_COST skill discount. */
export function effectiveAmplifierCost(session: GameSession): number {
  return Math.max(1, Math.floor(amplifierCost(session.amplifiersBuilt) * (1 - 0.01 * session.skillEffects.buildingCost)));
}

/** Effective trap cost with BUILDING_COST skill discount. */
export function effectiveTrapCost(session: GameSession): number {
  return Math.max(1, Math.floor(trapCost(session.trapsBuilt) * (1 - 0.01 * session.skillEffects.buildingCost)));
}

// --- Shrine cost (linear: 900 + 500*n) ---

export function shrineCost(shrinesBuilt: number): number {
  return SHRINE_BASE_COST + SHRINE_COST_INCREMENT * shrinesBuilt;
}

/** Effective shrine cost with BUILDING_COST skill discount. */
export function effectiveShrineCost(session: GameSession): number {
  return Math.max(1, Math.floor(shrineCost(session.shrinesBuilt) * (1 - 0.01 * session.skillEffects.buildingCost)));
}

// --- Build waves from config ---

/** Determine monster type for a given wave index (1-based rules matching AS3 canon). */
export function waveMonsterType(waveIndex: number): MonsterType {
  // Giant: every 7th wave starting at 7 (indices 6, 13, 20, ...). Rarest, highest priority.
  const isGiant = waveIndex >= 6 && (waveIndex - 6) % 7 === 0;
  // Runner: every 4th wave starting at 4 (indices 3, 7, 11, ...). Mid-priority.
  const isRunner = waveIndex >= 3 && (waveIndex - 3) % 4 === 0;
  // Armored: every 5th wave starting at 5 (indices 4, 9, 14, ...).
  const isArmored = waveIndex >= 4 && (waveIndex - 4) % 5 === 0;
  // Swarm: every 3rd wave starting at 3 (indices 2, 5, 8, ...).
  const isSwarm = waveIndex >= 2 && (waveIndex - 2) % 3 === 0;
  // Priority: Giant > Armored > Runner > Swarm > Normal.
  if (isGiant) return "Giant";
  if (isArmored) return "Armored";
  if (isRunner) return "Runner";
  if (isSwarm) return "Swarm";
  return "Normal";
}

export function buildWaves(config: FieldConfig): Wave[] {
  const wavesNum = config.wavesNum ?? DEFAULT_WAVES_NUM;
  const monstersPerWave = config.monstersPerWave ?? DEFAULT_MONSTERS_PER_WAVE;
  const intervalMs = config.spawnIntervalMs ?? DEFAULT_SPAWN_INTERVAL_MS;
  const firstDelay = config.firstWaveDelayMs ?? 0;
  const interWaveDelay = config.interWaveDelayMs ?? 0;

  // Entry cells: authored multi-entry or fallback to single entry from waypoints[0].
  const entries = config.entryCells && config.entryCells.length > 0
    ? config.entryCells
    : config.waypoints && config.waypoints.length > 0
      ? [config.waypoints[0]]
      : [{ row: 0, col: 0 }]; // ultimate fallback (should not happen in production)
  const E = entries.length;

  const waves: Wave[] = [];
  let waveStartAt = firstDelay;
  for (let i = 0; i < wavesNum; i++) {
    const isBoss = (i + 1) % 10 === 0;
    // Boss waves force Giant; otherwise follow the schedule.
    const monsterType = isBoss ? "Giant" : waveMonsterType(i);
    // Boss waves get a small squad (Giant range low) regardless of regular count.
    // Swarm: scale with field difficulty instead of flat 40 so starter fields
    // don't face a wall of mobs on wave 2.
    const waveMonsters = isBoss
      ? MONSTER_SPECS.Giant.countRange[1]
      : monsterType === "Swarm"
        ? Math.min(MONSTER_SPECS.Swarm.countRange[0], Math.max(monstersPerWave * 2, 10))
        : monsterType === "Giant"
          ? MONSTER_SPECS.Giant.countRange[0]
          : monstersPerWave;
    const waveIntervalMs = isBoss ? Math.round(intervalMs * 1.4) : intervalMs;

    // Distribute waveMonsters across entries.
    const spawners: WaveSpawner[] = [];
    if (waveMonsters >= E) {
      const base = Math.floor(waveMonsters / E);
      const extra = waveMonsters % E;
      for (let ei = 0; ei < E; ei++) {
        const total = base + (ei < extra ? 1 : 0);
        spawners.push({ entryCell: { ...entries[ei] }, total, spawned: 0, nextSpawnAt: waveStartAt });
      }
    } else {
      // N < E: only first N entries get 1 monster each, rest are skipped.
      for (let ei = 0; ei < waveMonsters; ei++) {
        spawners.push({ entryCell: { ...entries[ei] }, total: 1, spawned: 0, nextSpawnAt: waveStartAt });
      }
    }

    waves.push({
      index: i,
      totalMonsters: waveMonsters,
      intervalMs: waveIntervalMs,
      spawners,
      monsterType,
      isBoss,
    });
    waveStartAt += waveMonsters * waveIntervalMs;
    if (i + 1 < wavesNum) waveStartAt += interWaveDelay;
  }
  return waves;
}

// --- Path recompute helper ---

function recomputePathCells(session: GameSession): void {
  const { rows, cols } = session.field;
  const start = session.field.entryCells[0];
  const end = session.field.exitCell;

  // Build allowed set from walkableCells.
  const allowed = new Set<string>();
  for (const c of session.field.walkableCells) {
    allowed.add(`${c.row},${c.col}`);
  }
  // Build blocked set from path-blocking buildings. Traps are walkable.
  const blocked = new Set<string>();
  for (const t of session.towers) {
    if (t.type === "Trap") continue;
    blocked.add(`${t.cell.row},${t.cell.col}`);
  }

  const result = bfsInAllowed(rows, cols, start, end, allowed, blocked);
  if (result && result.length > 0) {
    session.pathCells = result;
  }
  // If result is null/empty, keep previous pathCells unchanged.
}

// --- Create session (ADR-7: no Orb, speed=0 prep phase) ---

export function createSession(
  fieldId: FieldId,
  config: FieldConfig,
  skillEffects?: SkillEffects,
  stageSettings?: StageSettings
): GameSession {
  const effects: SkillEffects = skillEffects ?? { ...DEFAULT_SKILL_EFFECTS };
  const settings: StageSettings = stageSettings ?? createDefaultStageSettings();
  const fieldBlockedSet = new Set<string>();
  for (const c of (config.blockedCells ?? [])) fieldBlockedSet.add(`${c.row},${c.col}`);

  // Phase D: derive navigation fields.
  // Prefer new fields (entryCells/exitCell/walkableCells) over deprecated waypoints.
  let entryCells: Cell[];
  let exitCell: Cell;
  let walkableCells: Cell[];
  let legacyWaypoints: Cell[];

  if (config.entryCells && config.entryCells.length > 0 && config.exitCell && config.walkableCells && config.walkableCells.length > 0) {
    // New-style config: all three new fields provided.
    entryCells = config.entryCells.map(c => ({ ...c }));
    exitCell = { ...config.exitCell };
    walkableCells = config.walkableCells.map(c => ({ ...c }));
    // Legacy compat: derive waypoints from entry/exit for deprecated Field fields.
    legacyWaypoints = [entryCells[0], exitCell];
  } else {
    // Legacy fallback: derive from waypoints.
    const waypoints = config.waypoints ?? [];
    if (waypoints.length < 2) {
      throw new Error(`createSession: FieldConfig must provide either (entryCells, exitCell, walkableCells) or waypoints with ≥ 2 points`);
    }
    const startCell = waypoints[0];
    const endCell = waypoints[waypoints.length - 1];
    const originalPathCells = computeFullPath(config.rows, config.cols, waypoints, fieldBlockedSet);
    entryCells = [{ ...startCell }];
    exitCell = { ...endCell };
    walkableCells = [...originalPathCells];
    legacyWaypoints = [...waypoints];
  }

  // Deprecated fields for backward-compat (tests + renderer that reads field.waypoints etc.)
  const startCell = legacyWaypoints[0];
  const endCell = legacyWaypoints[legacyWaypoints.length - 1];
  // originalPathCells: same as walkableCells for compat (deprecated).
  const originalPathCells = walkableCells;

  const field: Field = {
    id: fieldId,
    rows: config.rows,
    cols: config.cols,
    waypoints: legacyWaypoints,
    startCell: { ...startCell },
    endCell: { ...endCell },
    blockedCells: config.blockedCells ? config.blockedCells.map(c => ({ ...c })) : [],
    originalPathCells,
    entryCells,
    exitCell,
    walkableCells,
    allowedGemTypes: config.allowedGemTypes ? [...config.allowedGemTypes] : undefined,
  };
  const baseWavesNum = config.wavesNum ?? DEFAULT_WAVES_NUM;
  const wavesNum = baseWavesNum + getExtraWaves(settings);
  // Build waves using modified wavesNum
  const configForWaves = { ...config, wavesNum };
  const waves = buildWaves(configForWaves);
  const baseStartingMana = config.initialMana ?? STARTING_MANA;
  const badgeMana = Math.max(0, effects.badgesToMana);
  const startingMana = Math.round(baseStartingMana * (1 + 0.01 * effects.moreInitialMana)) + badgeMana;
  const maxMana = Math.round(
    Math.max(STARTING_MAX_MANA, baseStartingMana) *
    (1 + 0.01 * effects.moreInitialMana) *
    (1 + 0.01 * effects.cheaperManaPoolAndUnlock)
  ) + badgeMana;
  const baseHp = config.monsterBaseHp ?? DEFAULT_MONSTER_BASE_HP;
  const session: GameSession = {
    field,
    mana: { current: startingMana, max: maxMana },
    waves,
    currentWaveIndex: 0,
    monsters: [],
    elapsedMs: 0,
    towers: [],
    gemInventory: [],
    speed: 0,
    won: null,
    towersBuilt: 0,
    wallsBuilt: 0,
    amplifiersBuilt: 0,
    trapsBuilt: 0,
    shrinesBuilt: 0,
    hpGrowthRate: hpGrowthRateForWaveCount(wavesNum),
    armorGrowthRate: config.armorGrowthRate ?? DEFAULT_ARMOR_GROWTH_RATE,
    monsterBaseHp: baseHp * getHpMultiplier(settings),
    monstersPerWave: config.monstersPerWave ?? DEFAULT_MONSTERS_PER_WAVE,
    shots: [],
    hitFlashes: [],
    skillEffects: effects,
    stageSettings: settings,
    originalWavesNum: config.wavesNum ?? DEFAULT_WAVES_NUM,
    originalMonsterBaseHp: config.monsterBaseHp ?? DEFAULT_MONSTER_BASE_HP,
    sessionStats: {
      killsThisBattle: 0,
      banishesThisBattle: 0,
      bombsCastThisBattle: 0,
      startedAt: 0,
      earlyHurryCount: 0,
      manaFromEarlyHurry: 0,
    },
    pathCells: [],
  };
  recomputePathCells(session);

  // Spawn prebuilt buildings (tutorial helpers — free, not counted as player-built).
  if (config.prebuiltBuildings) {
    for (let i = 0; i < config.prebuiltBuildings.length; i++) {
      const { cell, type } = config.prebuiltBuildings[i];
      session.towers.push({
        id: `tower-prebuilt-${i}`,
        cell: { ...cell },
        type,
        gemSlots: [{ gem: null }],
        fireCooldownMs: 0,
        fireRateMs: FIRE_RATE_MS,
        originalCost: 0,
      });
    }
    recomputePathCells(session);
  }

  if (effects.wildStarterGem > 0) {
    const allowedTypes = field.allowedGemTypes && field.allowedGemTypes.length > 0
      ? field.allowedGemTypes
      : (Object.keys(GEM_SPECS) as GemType[]);
    const idx = Math.min(allowedTypes.length - 1, Math.floor(nextRandom(session) * allowedTypes.length));
    session.gemInventory.push({
      id: nextGemId(),
      type: allowedTypes[idx],
      level: Math.min(MAX_GEM_LEVEL, Math.max(1, Math.round(effects.wildStarterGem))),
    });
  }

  return session;
}

// --- Cell helpers ---

export function isCellInBounds(session: GameSession, cell: Cell): boolean {
  const { rows, cols } = session.field;
  return cell.row >= 0 && cell.row < rows && cell.col >= 0 && cell.col < cols;
}

export function isCellFree(session: GameSession, cell: Cell): boolean {
  return !session.towers.some(
    (t) => t.cell.row === cell.row && t.cell.col === cell.col
  );
}

/** True if the cell is a field obstacle (rock) — unbuildable and impassable. */
export function isCellBlockedByField(session: GameSession, cell: Cell): boolean {
  return session.field.blockedCells.some(
    (c) => c.row === cell.row && c.col === cell.col
  );
}

function isPathEndpoint(session: GameSession, cell: Cell): boolean {
  if (cell.row === session.field.exitCell.row && cell.col === session.field.exitCell.col) return true;
  return session.field.entryCells.some((entry) => entry.row === cell.row && entry.col === cell.col);
}

/**
 * True if a blocking building at `cell` would still leave at least one 4-connected path
 * from EVERY entry cell to the exit cell.
 * For multi-entry fields: returns true if ANY entry loses its path to exit.
 * Towers count as impassable; proposed cell is treated as blocked.
 * Entry cells and exit cell are always passable.
 */
export function wouldBlockPath(session: GameSession, proposedCell: Cell): boolean {
  const { rows, cols } = session.field;
  const entryCells = session.field.entryCells;
  const exitCell = session.field.exitCell;

  // Building on any entry or on the exit is always forbidden.
  if (proposedCell.row === exitCell.row && proposedCell.col === exitCell.col) return true;
  for (const entry of entryCells) {
    if (proposedCell.row === entry.row && proposedCell.col === entry.col) return true;
  }

  const idx = (r: number, c: number) => r * cols + c;
  const blocked = new Set<number>();
  for (const t of session.towers) {
    if (t.type === "Trap") continue;
    blocked.add(idx(t.cell.row, t.cell.col));
  }
  for (const c of session.field.blockedCells) blocked.add(idx(c.row, c.col));
  blocked.add(idx(proposedCell.row, proposedCell.col));
  // entry cells and exit always passable
  for (const entry of entryCells) blocked.delete(idx(entry.row, entry.col));
  blocked.delete(idx(exitCell.row, exitCell.col));

  // If ANY entry loses its path to exit → block is true.
  for (const entry of entryCells) {
    if (!bfsReachable(rows, cols, entry, exitCell, (r, c) => !blocked.has(idx(r, c)))) {
      return true;
    }
  }
  return false;
}

export function canPlaceBuilding(
  session: GameSession,
  cell: Cell,
  buildingType: BuildingType
): boolean {
  if (!isCellInBounds(session, cell)) return false;
  if (!isCellFree(session, cell)) return false;
  if (isCellBlockedByField(session, cell)) return false;
  if (buildingType === "Trap" && isPathEndpoint(session, cell)) return false;
  if (buildingType === "Trap") return true;
  if (wouldBlockPath(session, cell)) return false;
  return true;
}

// --- Mana (ADR-7: capped at max) ---

export function spendMana(session: GameSession, amount: number): boolean {
  if (session.mana.current < amount) return false;
  session.mana.current -= amount;
  return true;
}

export function addMana(session: GameSession, amount: number): void {
  session.mana.current = Math.min(session.mana.max, session.mana.current + amount);
}

// --- Tower (escalating cost) ---

/**
 * Returns true if any monster currently occupies the given cell (integer grid position).
 * Uses Math.floor to map continuous position to discrete cell.
 */
export function cellHasMonster(session: GameSession, cell: Cell): boolean {
  return session.monsters.some(
    (m) => Math.floor(m.position.row) === cell.row && Math.floor(m.position.col) === cell.col
  );
}

/**
 * Returns true if placing a structure at `cell` would leave no path through
 * (walkableCells ∖ towers ∖ {cell}) for ANY entry to exit.
 * If `cell` is not in walkableCells (i.e. holy ground) returns false immediately —
 * holy cells are always buildable from a path perspective.
 */
function wouldForceOffOriginalPath(session: GameSession, cell: Cell): boolean {
  const { rows, cols } = session.field;
  const entryCells = session.field.entryCells;
  const exitCell = session.field.exitCell;

  // Build allowed set from walkableCells.
  const allowed = new Set<number>();
  const idx = (r: number, c: number) => r * cols + c;
  for (const c of session.field.walkableCells) allowed.add(idx(c.row, c.col));

  // If the cell is not on the walkable path, building here is always ok path-wise.
  if (!allowed.has(idx(cell.row, cell.col))) return false;

  // Build blocked set: existing towers + the proposed cell (entries/exit stay open).
  const blocked = new Set<number>();
  for (const t of session.towers) {
    if (t.type === "Trap") continue;
    blocked.add(idx(t.cell.row, t.cell.col));
  }
  blocked.add(idx(cell.row, cell.col));
  for (const entry of entryCells) blocked.delete(idx(entry.row, entry.col));
  blocked.delete(idx(exitCell.row, exitCell.col));

  // If ANY entry loses its path to exit → block.
  for (const entry of entryCells) {
    if (!bfsReachable(rows, cols, entry, exitCell, (r, c) => {
      const i = idx(r, c);
      return allowed.has(i) && !blocked.has(i);
    })) {
      return true;
    }
  }
  return false;
}

let towerIdCounter = 0;
export function resetTowerIdCounter(): void {
  towerIdCounter = 0;
}

export function addTower(
  session: GameSession,
  cell: Cell,
  buildingType: BuildingType
): boolean {
  if (cellHasMonster(session, cell)) return false;
  if (wouldForceOffOriginalPath(session, cell)) return false;
  const cost = towerCost(session.towersBuilt);
  session.towers.push({
    id: `tower-${++towerIdCounter}`,
    cell: { ...cell },
    type: buildingType,
    gemSlots: [{ gem: null }],
    fireCooldownMs: 0,
    fireRateMs: FIRE_RATE_MS,
    originalCost: cost,
  });
  session.towersBuilt += 1;
  // Invalidate all monster BFS paths since terrain changed.
  for (const m of session.monsters) m.targetCell = null;
  recomputePathCells(session);
  return true;
}

export function addWall(session: GameSession, cell: Cell): boolean {
  if (cellHasMonster(session, cell)) return false;
  if (wouldForceOffOriginalPath(session, cell)) return false;
  const cost = wallCost(session.wallsBuilt);
  session.towers.push({
    id: `tower-${++towerIdCounter}`,
    cell: { ...cell },
    type: "Wall",
    gemSlots: [],
    fireCooldownMs: 0,
    fireRateMs: 0,
    originalCost: cost,
  });
  session.wallsBuilt += 1;
  // Invalidate all monster BFS paths since terrain changed.
  for (const m of session.monsters) m.targetCell = null;
  recomputePathCells(session);
  return true;
}

export function addAmplifier(session: GameSession, cell: Cell): boolean {
  if (cellHasMonster(session, cell)) return false;
  if (wouldForceOffOriginalPath(session, cell)) return false;
  const cost = amplifierCost(session.amplifiersBuilt);
  session.towers.push({
    id: `tower-${++towerIdCounter}`,
    cell: { ...cell },
    type: "Amplifier",
    gemSlots: [],
    fireCooldownMs: 0,
    fireRateMs: 0,
    originalCost: cost,
  });
  session.amplifiersBuilt += 1;
  // Invalidate all monster BFS paths since terrain changed.
  for (const m of session.monsters) m.targetCell = null;
  recomputePathCells(session);
  return true;
}

export function addTrap(session: GameSession, cell: Cell): boolean {
  if (cellHasMonster(session, cell)) return false;
  if (isPathEndpoint(session, cell)) return false;
  const cost = trapCost(session.trapsBuilt);
  session.towers.push({
    id: `tower-${++towerIdCounter}`,
    cell: { ...cell },
    type: "Trap",
    gemSlots: [{ gem: null }],
    fireCooldownMs: 0,
    fireRateMs: FIRE_RATE_MS,
    originalCost: cost,
  });
  session.trapsBuilt += 1;
  // Invalidate all monster BFS paths since terrain changed.
  for (const m of session.monsters) m.targetCell = null;
  recomputePathCells(session);
  return true;
}

export function addShrine(session: GameSession, cell: Cell): boolean {
  if (cellHasMonster(session, cell)) return false;
  if (wouldForceOffOriginalPath(session, cell)) return false;
  const cost = shrineCost(session.shrinesBuilt);
  session.towers.push({
    id: `tower-${++towerIdCounter}`,
    cell: { ...cell },
    type: "Shrine",
    gemSlots: [{ gem: null }],
    fireCooldownMs: 0,
    fireRateMs: 0,
    originalCost: cost,
    lastDischargeAt: 0,
  });
  session.shrinesBuilt += 1;
  // Invalidate all monster BFS paths since terrain changed.
  for (const m of session.monsters) m.targetCell = null;
  recomputePathCells(session);
  return true;
}

// --- Speed control (ADR-8) ---

export function setSpeed(session: GameSession, speed: SpeedMultiplier): void {
  session.speed = speed;
}

export function isPaused(session: GameSession): boolean {
  return session.speed === 0;
}

export function setWon(session: GameSession, won: boolean): void {
  session.won = won;
}

// --- Session stats increments ---

export function incrementSessionKills(session: GameSession, n: number = 1): void {
  session.sessionStats.killsThisBattle += n;
}

export function incrementSessionBanishes(session: GameSession, n: number = 1): void {
  session.sessionStats.banishesThisBattle += n;
}

export function incrementSessionBombs(session: GameSession, n: number = 1): void {
  session.sessionStats.bombsCastThisBattle += n;
}

export function recordSessionEarlyHurryReward(session: GameSession, mana: number): void {
  if (mana <= 0) return;
  session.sessionStats.earlyHurryCount += 1;
  session.sessionStats.manaFromEarlyHurry += mana;
}

/**
 * Light (mana) bonus for pulling the next spawn earlier (AS3 IngameCore wave brick path).
 * Base 10 × pow(1.6 + waveIndex×0.54, 1.214) × percent_saved / 100; percent from savedMs vs spawn interval.
 */
export function earlyHurryManaFromSavedAs3(waveIndex: number, savedMs: number, intervalMs: number): number {
  if (savedMs <= 0) return 0;
  const iv = Math.max(1, intervalMs);
  const pct = Math.min(100, Math.max(1, Math.round((100 * savedMs) / iv)));
  const wf = Math.round(Math.pow(1.6 + waveIndex * 0.54, 1.214) * 10) / 10;
  return Math.round((10 * wf * pct) / 100);
}

// --- Gem costs & operations ---

let gemIdCounter = 0;
function nextGemId(): string {
  return `gem-${++gemIdCounter}`;
}

export function resetGemIdCounter(): void {
  gemIdCounter = 0;
}

export function createGem(session: GameSession, gemType: GemType): Gem | null {
  if (session.gemInventory.length >= INVENTORY_MAX) return null;
  const cost = effectiveGemCreateCost(session);
  if (session.mana.current < cost) return null;
  session.mana.current -= cost;
  const gem: Gem = { id: nextGemId(), type: gemType, level: 1 };
  // TRIPLE_GEM_MASTERY: chance to start at level 3
  const tripleChance = Math.min(1.0, 0.01 * session.skillEffects.tripleGemMastery);
  if (tripleChance > 0 && nextRandom(session) < tripleChance) {
    gem.level = 3;
  }
  session.gemInventory.push(gem);
  return gem;
}

export function findGemInInventory(session: GameSession, gemId: string): Gem | null {
  return session.gemInventory.find((g) => g.id === gemId) ?? null;
}

export function removeGemFromInventory(session: GameSession, gemId: string): boolean {
  const idx = session.gemInventory.findIndex((g) => g.id === gemId);
  if (idx === -1) return false;
  session.gemInventory.splice(idx, 1);
  return true;
}

export function placeGemInTower(
  session: GameSession,
  gemId: string,
  towerId: string,
  slotIndex: number
): boolean {
  const gem = findGemInInventory(session, gemId);
  if (!gem) return false;
  const tower = session.towers.find((t) => t.id === towerId);
  if (!tower) return false;
  const slot = tower.gemSlots[slotIndex];
  if (!slot || slot.gem !== null) return false;
  removeGemFromInventory(session, gemId);
  slot.gem = gem;
  return true;
}

export function upgradeGemInSlot(
  session: GameSession,
  towerId: string,
  slotIndex: number
): boolean {
  const tower = session.towers.find((t) => t.id === towerId);
  if (!tower) return false;
  const slot = tower.gemSlots[slotIndex];
  if (!slot?.gem) return false;
  if (slot.gem.level >= MAX_GEM_LEVEL) return false;
  const cost = gemUpgradeCost(slot.gem.level);
  if (session.mana.current < cost) return false;
  session.mana.current -= cost;
  slot.gem.level += 1;
  return true;
}

export function duplicateGemInSlot(
  session: GameSession,
  towerId: string,
  slotIndex: number
): boolean {
  const tower = session.towers.find((t) => t.id === towerId);
  if (!tower) return false;
  const slot = tower.gemSlots[slotIndex];
  if (!slot?.gem) return false;
  const cost = gemDuplicateCost(slot.gem.level);
  if (session.mana.current < cost) return false;
  session.mana.current -= cost;
  const copy: Gem = { id: nextGemId(), type: slot.gem.type, level: slot.gem.level };
  session.gemInventory.push(copy);
  return true;
}

/**
 * Combine two gems in the inventory into one (pure state transition).
 * Free (no mana cost — sacrifice of the second gem is the cost).
 *
 * Three cases (A = apex = higher level; on ties either is apex):
 *  - Case 1 (same type + level): A.level += 1, B consumed.
 *  - Case 2 (different type + same level): A.level += 1, A becomes dual-type
 *    (secondaryType = B.type), mods = 0.70 × both primary and secondary
 *    effect multipliers, B consumed.
 *  - Case 3 (different level): A keeps its level; mods accumulate
 *    multiplicatively (+5% dmg, -3% fire rate, -5% T1 effect, 50% T2 magnitude,
 *    secondaryProcChance = 0.08 × L_low/L_high). A.secondaryType = B.type
 *    (otherwise secondaryProcChance is meaningless). B consumed.
 *
 * MVP: refuses triples — either input already being dual returns AlreadyDual.
 * See BUGS/design_combine.md and src/domain/combine-constants.ts.
 */
export function combineGems(
  session: GameSession,
  gemAId: string,
  gemBId: string
): Gem | CombineGemError {
  if (gemAId === gemBId) return { code: "SameGem" };
  const g1 = findGemInInventory(session, gemAId);
  const g2 = findGemInInventory(session, gemBId);
  if (!g1 || !g2) return { code: "NotFound" };
  if (g1.secondaryType !== undefined || g2.secondaryType !== undefined) {
    return { code: "AlreadyDual" };
  }

  // Sort so that A is apex (higher level); on ties, g1 is apex.
  const [A, B] = g1.level >= g2.level ? [g1, g2] : [g2, g1];

  if (A.type === B.type && A.level === B.level) {
    // Case 1: same type + same level → L+1
    if (A.level >= MAX_GEM_LEVEL) return { code: "AlreadyMaxLevel" };
    A.level += 1;
    session.gemInventory = session.gemInventory.filter((g) => g.id !== B.id);
    return A;
  }

  if (A.type !== B.type && A.level === B.level) {
    // Case 2: different type + same level → L+1 + dual-type + 0.70 penalty
    if (A.level >= MAX_GEM_LEVEL) return { code: "AlreadyMaxLevel" };
    A.level += 1;
    A.secondaryType = B.type;
    A.mods = {
      primaryEffectMult: DUAL_EFFECT_PENALTY,
      secondaryEffectMult: DUAL_EFFECT_PENALTY,
    };
    session.gemInventory = session.gemInventory.filter((g) => g.id !== B.id);
    return A;
  }

  if (A.level > B.level) {
    // Case 3: different level → apex accumulates mods, keeps its own level.
    const mods: GemMods = A.mods ?? {};
    mods.dmgMult = (mods.dmgMult ?? 1) * APEX_DMG_MULT;
    mods.fireRateMult = (mods.fireRateMult ?? 1) * APEX_FIRE_RATE_MULT;
    mods.primaryEffectMult = (mods.primaryEffectMult ?? 1) * APEX_PRIMARY_PENALTY;
    mods.secondaryEffectMult = APEX_SECONDARY_MAGNITUDE;
    mods.secondaryProcChance = APEX_SECONDARY_PROC_BASE * (B.level / A.level);
    A.mods = mods;
    // secondaryProcChance needs a type to proc on — mark A as dual-type.
    A.secondaryType = B.type;
    session.gemInventory = session.gemInventory.filter((g) => g.id !== B.id);
    return A;
  }

  // Unreachable: after sorting A.level >= B.level, and all (type, level)
  // combinations are covered above.
  throw new Error("combineGems: unreachable case");
}

export function recycleGemFromSlot(
  session: GameSession,
  towerId: string,
  slotIndex: number
): number {
  const tower = session.towers.find((t) => t.id === towerId);
  if (!tower) return 0;
  const slot = tower.gemSlots[slotIndex];
  if (!slot?.gem) return 0;
  const value = gemRecycleValue(slot.gem.level);
  addMana(session, value);
  slot.gem = null;
  return value;
}

/**
 * Remove a tower, refund 50% of the original build cost, return any
 * installed gem to inventory. Returns refund amount or 0 if tower not found.
 * Tower's build cost is reconstructed from its position in towers array.
 * Note: towersBuilt is NOT decremented (AS3: cost only grows, no spam exploit).
 */
export function demolishTower(session: GameSession, towerId: string): number {
  const idx = session.towers.findIndex(t => t.id === towerId);
  if (idx < 0) return 0;
  const tower = session.towers[idx];
  // Refund: (50% + demolition bonus%) of the original build cost, capped at 100%.
  const refundRatio = Math.min(1.0, 0.5 + 0.01 * session.skillEffects.demolition);
  const refund = Math.floor(tower.originalCost * refundRatio);
  addMana(session, refund);
  // Return installed gem to inventory (walls have no gem slots, so this is a no-op for them)
  const gem = tower.gemSlots[0]?.gem;
  if (gem) {
    session.gemInventory.push(gem);
  }
  // Remove tower/wall
  session.towers.splice(idx, 1);
  // Invalidate pathfinding — removed obstacle, monsters may take shorter paths
  for (const m of session.monsters) m.targetCell = null;
  recomputePathCells(session);
  return refund;
}

/** Remove gem from tower slot, return it to inventory. Returns the gem or null. */
export function removeGemFromTower(
  session: GameSession,
  towerId: string,
  slotIndex: number
): Gem | null {
  const tower = session.towers.find((t) => t.id === towerId);
  if (!tower) return null;
  const slot = tower.gemSlots[slotIndex];
  if (!slot?.gem) return null;
  const gem = slot.gem;
  slot.gem = null;
  session.gemInventory.push(gem);
  return gem;
}

/** Swap gems in slot 0 of two towers. Returns false if either tower not found or slot empty. */
export function swapGemsBetweenTowers(
  session: GameSession,
  towerId1: string,
  towerId2: string
): boolean {
  if (towerId1 === towerId2) return false;
  const t1 = session.towers.find((t) => t.id === towerId1);
  const t2 = session.towers.find((t) => t.id === towerId2);
  if (!t1 || !t2) return false;
  const slot1 = t1.gemSlots[0];
  const slot2 = t2.gemSlots[0];
  if (!slot1 || !slot2 || !slot1.gem || !slot2.gem) return false;
  const tmp = slot1.gem;
  slot1.gem = slot2.gem;
  slot2.gem = tmp;
  return true;
}

// --- Waves & monsters (AS3-faithful: exponential HP, armor from wave 3) ---

let monsterIdCounter = 0;
function nextMonsterId(): string {
  return `monster-${++monsterIdCounter}`;
}
export function resetMonsterIdCounter(): void {
  monsterIdCounter = 0;
}

/**
 * Banish monster that reached the orb: deduct mana, then respawn at start with current HP
 * and increased costToBanishMultiplier (AS3-faithful: ×1.68, or ×2.63 with extraBanishCost).
 * HP/status effects (slow/poison/shock/armor) are NOT reset — AS3 keeps them.
 * With healOnReturn: monster heals 50% of maxHp on respawn.
 */
export function banishMonster(session: GameSession, monster: Monster): number {
  const cost = Math.round(
    monster.manaReward * 2 * monster.costToBanishMultiplier *
    (1 - 0.01 * session.skillEffects.banishManaCost)
  );
  session.mana.current = Math.max(0, session.mana.current - cost);
  const growth = getBanishMultiplierGrowth(session.stageSettings);
  monster.costToBanishMultiplier *= growth;
  // Respawn at the entry cell this monster came from; fallback to first entry.
  const start = monster.entryCell ?? session.field.entryCells[0];
  monster.position = { row: start.row, col: start.col };
  monster.currentCell = { row: start.row, col: start.col };
  monster.targetCell = null; // force BFS recompute
  // healOnReturn: heal 50% of maxHp on banishment (AS3: isReturningMonstersHealingSelected)
  if (session.stageSettings.healOnReturn) {
    monster.hp = Math.min(monster.maxHp, monster.hp + 0.5 * monster.maxHp);
  }
  session.monsters.push(monster);
  incrementSessionBanishes(session, 1);
  return cost;
}

/** Spawn one monster if current wave is ready. HP = baseHp × pow(rate, waveIndex). */
export function spawnNextInWave(session: GameSession, elapsedMs: number): boolean {
  const waves = session.waves;
  if (session.currentWaveIndex >= waves.length) return false;
  const wave = waves[session.currentWaveIndex];
  if (waveDone(wave)) return false;

  // Find the spawner with the minimum nextSpawnAt that is ready and has monsters left.
  let bestSpawner = null as (typeof wave.spawners[0]) | null;
  for (const sp of wave.spawners) {
    if (sp.spawned >= sp.total) continue;
    if (elapsedMs < sp.nextSpawnAt) continue;
    if (bestSpawner === null || sp.nextSpawnAt < bestSpawner.nextSpawnAt) {
      bestSpawner = sp;
    }
  }
  if (bestSpawner === null) return false;

  const spec = MONSTER_SPECS[wave.monsterType];
  const bossHpMult = wave.isBoss ? 2 : 1;
  const bossRewardMult = wave.isBoss ? 2 : 1;
  const totalMonsters = waveTotal(wave);
  const hp = Math.max(1, Math.round(session.monsterBaseHp * Math.pow(session.hpGrowthRate, wave.index) * spec.hpMult * bossHpMult));
  // AS3 IngamePopulator.as:300 — rate*(w-3) + rand*(w-3)*rate with rand uniform in [-1, 1].
  // Gives armor ∈ [0, 2×base] with ±base noise; deterministic via seeded rng.
  const armorBase = session.armorGrowthRate * Math.max(0, wave.index - 3) * spec.armorMult;
  const armorRnd = armorBase > 0 ? (nextRandom(session) - 0.5) * 2 : 0;
  const armor = Math.max(0, Math.round(armorBase * (1 + armorRnd)));
  const manaReward = Math.max(1, Math.round(12 * (10 + wave.index * 0.65) / totalMonsters * spec.rewardMult * bossRewardMult));

  const entryCell = bestSpawner.entryCell;
  const monster: Monster = {
    id: nextMonsterId(),
    position: { row: entryCell.row, col: entryCell.col },
    currentCell: { row: entryCell.row, col: entryCell.col },
    targetCell: null, // BFS will compute on first move
    entryCell: { row: entryCell.row, col: entryCell.col },
    hp,
    maxHp: hp,
    armor,
    costToBanishMultiplier: 1,
    manaReward,
    type: wave.monsterType,
    dirIndex: 0,
  };
  session.monsters.push(monster);
  bestSpawner.spawned += 1;
  bestSpawner.nextSpawnAt += wave.intervalMs;
  if (waveDone(wave)) {
    session.currentWaveIndex += 1;
  }
  return true;
}

export type HurryNextMonsterSpawnResult =
  | { ok: false }
  | { ok: true; savedMs: number };

/**
 * Pull the next spawn time for the active wave to now so the next monster
 * appears as soon as the game loop runs (HUD «call wave» / hurry).
 * Returns saved wait time (ms) for mana reward when > 0 (AS3 early-wave bonus).
 * For multi-entry: targets the spawner with the minimum nextSpawnAt that still has monsters.
 */
export function hurryNextMonsterSpawn(session: GameSession): HurryNextMonsterSpawnResult {
  const waves = session.waves;
  if (session.currentWaveIndex >= waves.length) return { ok: false };
  const wave = waves[session.currentWaveIndex];
  if (waveDone(wave)) return { ok: false };

  // Find spawner with minimum nextSpawnAt that still has monsters.
  let bestSpawner = null as (typeof wave.spawners[0]) | null;
  for (const sp of wave.spawners) {
    if (sp.spawned >= sp.total) continue;
    if (bestSpawner === null || sp.nextSpawnAt < bestSpawner.nextSpawnAt) {
      bestSpawner = sp;
    }
  }
  if (bestSpawner === null) return { ok: false };

  const savedMs = bestSpawner.nextSpawnAt - session.elapsedMs;
  bestSpawner.nextSpawnAt = session.elapsedMs;
  if (savedMs <= 0) return { ok: false };
  return { ok: true, savedMs };
}

/**
 * Advance monsters by deltaMs using BFS-based pathfinding (lazy recompute per cell).
 * Each cell crossing takes SPEED_MS_PER_CELL. Remainders carry over between cells.
 * Returns monsters that reached the altar (caller banishes them from session).
 */
export function tickMonsterMovement(session: GameSession, deltaMs: number): Monster[] {
  const altar = session.field.exitCell;
  const reached: Monster[] = [];
  const remaining: Monster[] = [];

  for (const m of session.monsters) {
    // Cyan SHOCK: skip movement entirely.
    if ((m.shockUntilMs ?? 0) > session.elapsedMs) {
      const atAltar =
        m.currentCell.row === altar.row && m.currentCell.col === altar.col;
      if (atAltar) reached.push(m);
      else remaining.push(m);
      continue;
    }

    // Purple SLOW: divide speed by (1+ratio) → same as multiplying time per cell.
    const slowed = (m.slowUntilMs ?? 0) > session.elapsedMs;
    const slowRatio = m.slowRatio ?? PURPLE_SLOW_RATIO;
    const effectiveDelta = slowed ? deltaMs / (1 + slowRatio) : deltaMs;
    const spec = MONSTER_SPECS[m.type ?? "Normal"];
    const effectiveMsPerCell = SPEED_MS_PER_CELL * spec.speedMult;
    let cellsBudget = effectiveDelta / effectiveMsPerCell;

    while (cellsBudget > 0) {
      // Shortest route to altar only (same graph as computeFullPath / dashed line).
      if (!m.targetCell) {
        const next = nextCellTowardsTarget(session, m.currentCell, altar);
        if (!next) break;
        m.targetCell = next;
      }

      const dr = m.targetCell.row - m.position.row;
      const dc = m.targetCell.col - m.position.col;

      // Update direction index 0..7 based on target vector (Row=Y, Col=X)
      // S=0, SE=1, E=2, NE=3, N=4, NW=5, W=6, SW=7
      if (Math.abs(dr) > 1e-6 || Math.abs(dc) > 1e-6) {
        const angle = Math.atan2(dr, dc);
        m.dirIndex = (2 - Math.round(angle / (Math.PI / 4)) + 8) % 8;
      }

      // Use Chebyshev distance for diagonal paths; here BFS is 4-connected so only Manhattan.
      const dist = Math.abs(dr) + Math.abs(dc);

      if (dist <= 1e-9) {
        // Arrived at targetCell.
        m.currentCell = { row: m.targetCell.row, col: m.targetCell.col };
        m.targetCell = null; // trigger BFS recompute for next cell
        if (m.currentCell.row === altar.row && m.currentCell.col === altar.col) {
          break;
        }
        continue;
      }

      if (cellsBudget >= dist) {
        m.position.row = m.targetCell.row;
        m.position.col = m.targetCell.col;
        cellsBudget -= dist;
        m.currentCell = { row: m.targetCell.row, col: m.targetCell.col };
        m.targetCell = null;
        if (m.currentCell.row === altar.row && m.currentCell.col === altar.col) {
          break;
        }
      } else {
        const step = cellsBudget / dist;
        m.position.row += dr * step;
        m.position.col += dc * step;
        cellsBudget = 0;
      }
    }

    const atAltar =
      m.currentCell.row === altar.row && m.currentCell.col === altar.col;
    if (atAltar) reached.push(m);
    else remaining.push(m);
  }

  session.monsters.length = 0;
  session.monsters.push(...remaining);
  return reached;
}

// --- Tower shooting, damage (plan 07) ---

export function removeMonster(session: GameSession, monsterId: string): void {
  session.monsters = session.monsters.filter((m) => m.id !== monsterId);
}

export function damageMonster(
  session: GameSession,
  monsterId: string,
  amount: number
): void {
  const monster = session.monsters.find((m) => m.id === monsterId);
  if (!monster) return;
  monster.hp = Math.max(0, monster.hp - amount);
  if (monster.hp <= 0) {
    addMana(session, monster.manaReward * (1 + 0.01 * session.skillEffects.manaReplenish));
    removeMonster(session, monsterId);
    incrementSessionKills(session, 1);
  }
}

let shotIdCounter = 0;
function nextShotId(): string {
  return `shot-${++shotIdCounter}`;
}
export function resetShotIdCounter(): void {
  shotIdCounter = 0;
}

let flashIdCounter = 0;
function nextFlashId(): string {
  return `flash-${++flashIdCounter}`;
}
export function resetFlashIdCounter(): void {
  flashIdCounter = 0;
}

/**
 * Plan 08: towers don't damage instantly — they spawn Shots that fly to targetPos.
 * Crit roll happens at shot-create time so damage payload is locked in.
 * Returns count of shots fired (for audio).
 */
export function tickTowerShooting(session: GameSession, deltaMs: number): { shots: number } {
  let shots = 0;
  for (const tower of session.towers) {
    tower.fireCooldownMs -= deltaMs;
    if (tower.fireCooldownMs <= 0) {
      const target = getTowerTarget(session, tower);
      if (target !== null) {
        const slot = tower.gemSlots.find((s) => s.gem !== null);
        if (!slot?.gem) {
          tower.fireCooldownMs = 0;
          continue;
        }
        const baseDmg = computeDamage(session, tower, target);
        const critMult = rollCritMultiplier(slot.gem.type, session);
        const damage = baseDmg * critMult;
        session.shots.push({
          id: nextShotId(),
          fromTowerId: tower.id,
          targetMonsterId: target.id,
          pos: { row: tower.cell.row, col: tower.cell.col },
          targetPos: { row: target.position.row, col: target.position.col },
          damage,
          gemType: slot.gem.type,
          gemLevel: slot.gem.level,
          speedCellsPerMs: SHOT_SPEED_CELLS_PER_MS,
          secondaryGemType: slot.gem.secondaryType,
          mods: slot.gem.mods,
        });
        shots += 1;
        tower.fireCooldownMs += getFireRateMs(session, tower);
      } else {
        tower.fireCooldownMs = 0;
      }
    }
  }
  return { shots };
}

/**
 * Plan 08: advance each shot toward targetPos. On arrival:
 * - find target monster (by id); may be null if dead/despawned
 * - if alive: damage it; apply gem effect
 * - if dead: shot fizzles silently (no effect)
 * - always spawn hit-flash
 * Returns count of kills (for audio).
 */
export function tickShots(session: GameSession, deltaMs: number): { kills: number } {
  if (deltaMs <= 0) return { kills: 0 };
  let kills = 0;
  const survivors: Shot[] = [];
  for (const shot of session.shots) {
    const dr = shot.targetPos.row - shot.pos.row;
    const dc = shot.targetPos.col - shot.pos.col;
    const dist = Math.hypot(dr, dc);
    const step = shot.speedCellsPerMs * deltaMs;
    if (step >= dist || dist < 1e-9) {
      // Arrival.
      const tower = session.towers.find((t) => t.id === shot.fromTowerId);
      const target = session.monsters.find((m) => m.id === shot.targetMonsterId) ?? null;
      const hpBefore = target?.hp ?? 0;
      if (target) {
        damageMonster(session, target.id, shot.damage);
        if (hpBefore > 0 && (session.monsters.find((m) => m.id === target.id)?.hp ?? 0) <= 0) {
          kills += 1;
        }
      }
      if (tower) {
        // Live target ref for effect (may have been removed by damageMonster above).
        const liveTarget = session.monsters.find((m) => m.id === shot.targetMonsterId) ?? null;
        applyGemEffectOnHit({
          session,
          tower,
          target: liveTarget ?? target,
          hitPos: shot.targetPos,
          baseDamage: shot.damage,
          gemType: shot.gemType,
          gemLevel: shot.gemLevel,
          secondaryGemType: shot.secondaryGemType,
          mods: shot.mods,
        });
        // Splash/chain may have killed additional monsters — count them.
      }
      session.hitFlashes.push({
        id: nextFlashId(),
        row: shot.targetPos.row,
        col: shot.targetPos.col,
        startMs: session.elapsedMs,
        durationMs: HIT_FLASH_DURATION_MS,
        gemType: shot.gemType,
      });
      // Shot consumed — not added to survivors.
    } else {
      shot.pos.row += (dr / dist) * step;
      shot.pos.col += (dc / dist) * step;
      survivors.push(shot);
    }
  }
  session.shots = survivors;
  return { kills };
}

/** Remove expired hit-flashes. */
export function tickHitFlashes(session: GameSession): void {
  if (session.hitFlashes.length === 0) return;
  session.hitFlashes = session.hitFlashes.filter(
    (f) => session.elapsedMs - f.startMs < f.durationMs
  );
}

/**
 * Tick shrine discharge: once per SHRINE_CHARGE_MS cycle, if any monster is in
 * radius, deal flat AoE damage to all in-range monsters and reset the charge timer.
 * Returns number of discharge events this tick.
 */
export function tickShrines(session: GameSession, _deltaMs: number): number {
  let discharges = 0;
  for (const tower of session.towers) {
    if (tower.type !== "Shrine") continue;
    const since = session.elapsedMs - (tower.lastDischargeAt ?? 0);
    if (since < SHRINE_CHARGE_MS) continue;
    // Find all monsters within Chebyshev radius
    const targets = session.monsters.filter((m) =>
      Math.max(
        Math.abs(m.position.row - tower.cell.row),
        Math.abs(m.position.col - tower.cell.col)
      ) <= SHRINE_RADIUS_CELLS
    );
    if (targets.length === 0) continue;
    // Discharge: flat damage to all in radius
    const damage = Math.round(SHRINE_DAMAGE_BASE * (1 + 0.01 * session.skillEffects.shrines));
    for (const m of targets) {
      damageMonster(session, m.id, damage);
    }
    // Golden AoE flash at shrine center
    session.hitFlashes.push({
      id: `flash-shrine-${tower.id}-${session.elapsedMs}`,
      row: tower.cell.row,
      col: tower.cell.col,
      startMs: session.elapsedMs,
      durationMs: 600,
      gemType: "Yellow",
    });
    tower.lastDischargeAt = session.elapsedMs;
    discharges++;
  }
  return discharges;
}

// --- Gem Bomb (AS3: IngameSpellCaster gem-bomb mechanic) ---

/**
 * Cast a gem as an area-damage bomb.
 * Removes the gem from inventory, damages up to maxTargets monsters
 * within Chebyshev radius of targetCell, refunds 70% of the gem's upgrade cost
 * as mana (AS3 MANA_GAIN_FROM_GEM mechanic), and spawns a hit flash at target.
 */
export function castGemBomb(
  session: GameSession,
  gemId: string,
  targetCell: Cell
): {
  ok: true;
  damage: number;
  hitMonsterIds: string[];
  manaRefunded: number;
} | { code: "GemNotFound" | "InvalidCell" } {
  const gem = findGemInInventory(session, gemId);
  if (!gem) return { code: "GemNotFound" };
  if (!isCellInBounds(session, targetCell)) return { code: "InvalidCell" };

  const spec = GEM_SPECS[gem.type];
  const roll = spec.damageMin + nextRandom(session) * (spec.damageMax - spec.damageMin);
  const baseDmg = Math.round(roll * gem.level * (1 + 0.01 * session.skillEffects.damage));
  const bombMult = 2.2 + gem.level * 1.5;
  const damage = Math.round(baseDmg * bombMult * (1 + 0.01 * session.skillEffects.violentExplosions));
  const maxTargets = Math.min(8, 2 + gem.level);
  const radius = 1.5;

  // Find monsters in Chebyshev radius, sorted by distance, up to maxTargets
  const inRadius = session.monsters
    .map((m) => ({
      m,
      d: Math.max(
        Math.abs(m.position.row - targetCell.row),
        Math.abs(m.position.col - targetCell.col)
      ),
    }))
    .filter((x) => x.d <= radius)
    .sort((a, b) => a.d - b.d)
    .slice(0, maxTargets);

  const hitMonsterIds = inRadius.map((x) => x.m.id);
  for (const { m } of inRadius) {
    damageMonster(session, m.id, damage);
  }

  // Refund 70% of the gem's upgrade cost (AS3: MANA_GAIN_FROM_GEM)
  const manaRefunded = Math.floor(gemUpgradeCost(gem.level) * 0.7);
  addMana(session, manaRefunded);

  // Consume gem
  removeGemFromInventory(session, gemId);

  // Visual: big hit flash at target cell
  session.hitFlashes.push({
    id: nextFlashId(),
    row: targetCell.row,
    col: targetCell.col,
    startMs: session.elapsedMs,
    durationMs: 400,
    gemType: gem.type,
  });

  incrementSessionBombs(session, 1);
  return { ok: true, damage, hitMonsterIds, manaRefunded };
}
